/*
 * rj-lazy.js — IntersectionObserver lazy image loader.
 *
 * Replaces lazysizes.min.js (29 KB, ~1,890 ms of main thread + 259 ms forced
 * reflow) and the inline offsetParent-walking IIFE that used to sit in
 * render_bottom.liquid (~782 ms of forced reflow). Speed audit fixes #6 and #9.
 *
 * Implements the attribute contract this theme actually uses:
 *   data-src / data-bgset  Shopify CDN URL emitted with `width=1`
 *   data-widths            JSON array of candidate widths
 *   data-optimumx          DPR cap
 *   data-sizes="auto"      resolve `sizes` from the laid-out box
 *   data-sizes-scale       multiplier for hover/secondary images
 *   classes                lazyloadt4s -> lazyloadt4sing -> lazyloadt4sed
 *
 * Above-the-fold images need no special casing: IntersectionObserver reports
 * them on first observation, which is what the old IIFE was hand-rolling.
 */
(function () {
  'use strict';

  var LOAD = 'lazyloadt4s';
  var LOADING = 'lazyloadt4sing';
  var LOADED = 'lazyloadt4sed';
  var SELECTOR = '.' + LOAD + ',[data-bgset]';
  /* lazysizes expanded ~370px and then pre-loaded the rest at idle. A wider margin
     keeps near-viewport images starting as early as they used to, without the
     load-everything pass that made the old script expensive. */
  var MARGIN = '800px';

  function candidates(el) {
    var raw = el.getAttribute('data-widths');
    if (!raw) return null;
    try {
      var list = JSON.parse(raw);
      if (!list || !list.length) return null;
      return list.slice().sort(function (a, b) { return a - b; });
    } catch (e) {
      return null;
    }
  }

  function boxWidth(el) {
    var w = el.clientWidth || el.offsetWidth || 0;
    if (!w && el.parentElement) w = el.parentElement.clientWidth || 0;
    return w || window.innerWidth || 800;
  }

  /* Swap the `width=1` placeholder for the smallest candidate that covers the box. */
  function sized(url, el) {
    var list = candidates(el);
    if (!url || !list) return url;
    var scale = parseFloat(el.getAttribute('data-sizes-scale')) || 1;
    var cap = parseFloat(el.getAttribute('data-optimumx')) || 2;
    var need = Math.ceil(boxWidth(el) * scale * Math.min(window.devicePixelRatio || 1, cap));
    var pick = list[list.length - 1];
    for (var i = 0; i < list.length; i++) {
      if (list[i] >= need) { pick = list[i]; break; }
    }
    return url.replace(/([?&]width=)\d+/, '$1' + pick);
  }

  function settle(el) {
    el.classList.remove(LOADING);
    el.classList.add(LOADED);
  }

  function unveil(el) {
    if (el.getAttribute('data-rj-lazy') === 'done') return;
    el.setAttribute('data-rj-lazy', 'done');
    el.classList.remove(LOAD);
    el.classList.add(LOADING);

    var bgset = el.getAttribute('data-bgset');
    if (bgset) {
      var bgUrl = sized(bgset, el);
      var pre = new Image();
      pre.onload = pre.onerror = function () {
        el.style.backgroundImage = 'url("' + bgUrl + '")';
        settle(el);
      };
      pre.src = bgUrl;
      return;
    }

    var srcset = el.getAttribute('data-srcset');
    if (srcset) el.setAttribute('srcset', srcset);

    var src = el.getAttribute('data-src');
    if (!src) { settle(el); return; }

    if (el.getAttribute('data-sizes') === 'auto') {
      el.setAttribute('sizes', boxWidth(el) + 'px');
    }
    el.removeAttribute('loading');

    if (el.tagName === 'IMG') {
      el.addEventListener('load', function () { settle(el); }, { once: true });
      el.addEventListener('error', function () { settle(el); }, { once: true });
      el.setAttribute('src', sized(src, el));
      return;
    }

    el.style.backgroundImage = 'url("' + sized(src, el) + '")';
    settle(el);
  }

  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        io.unobserve(entries[i].target);
        unveil(entries[i].target);
      }
    }, { rootMargin: MARGIN });
  }

  /* Above-the-fold grid images should not wait for an IntersectionObserver callback.
     The first few images inside #MainContent are unveiled directly, on a rAF so the
     box has been laid out and sized() picks the grid-item width rather than falling
     back to the full viewport. Hover images are skipped - they are never visible
     on first paint and would otherwise eat the budget. */
  var EAGER_MAX = (window.innerWidth || 1024) < 768 ? 4 : 8;
  var eagerCount = 0;
  var eagerQueue = [];
  var eagerScheduled = false;

  function eagerCandidate(el) {
    return el.tagName === 'IMG' &&
      eagerCount + eagerQueue.length < EAGER_MAX &&
      el.getAttribute('data-src') &&
      !el.hasAttribute('data-pr-img2') &&
      el.className.indexOf('t4s-product-hover-img') === -1 &&
      el.closest && el.closest('#MainContent');
  }

  function flushEager() {
    eagerScheduled = false;
    var queued = eagerQueue;
    eagerQueue = [];
    for (var i = 0; i < queued.length; i++) {
      eagerCount++;
      if (io) io.unobserve(queued[i]);
      unveil(queued[i]);
    }
  }

  function queueEager(el) {
    eagerQueue.push(el);
    if (eagerScheduled) return;
    eagerScheduled = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(flushEager);
    else setTimeout(flushEager, 0);
  }

  function observe(el) {
    if (el.getAttribute('data-rj-lazy')) return;
    el.setAttribute('data-rj-lazy', 'seen');
    if (eagerCandidate(el)) { queueEager(el); return; }
    if (io) io.observe(el); else unveil(el);
  }

  function scan(root) {
    var nodes = (root || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) observe(nodes[i]);
  }

  scan(document);

  /* Product cards, quick view and facet re-renders inject markup after load. */
  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SELECTOR)) observe(node);
          if (node.querySelectorAll) scan(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* facets.min.js calls lazySizesT4.autoSizer.checkElems() after a column change. */
  window.lazySizesT4 = window.lazySizesT4 || { autoSizer: { checkElems: function () { scan(document); } } };
  window.rjLazy = { scan: scan, unveil: unveil };
})();
